import React, { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useNotification } from '../contexts/NotificationContext';

/**
 * Protected Route Component
 * Wraps routes that require authentication
 * 
 * @param {Object} props
 * @param {React.ReactNode} props.children - Component to render if authenticated
 * @param {string[]} props.roles - Optional array of required roles
 * @param {boolean} props.requireAll - If true, user must have all roles (default: false)
 */
const ProtectedRoute = ({ children, roles = null, requireAll = false }) => {
  const { isAuthenticated, loading, hasAnyRole, hasAllRoles } = useAuth();
  const { showError } = useNotification();
  const location = useLocation();

  // Calculate role requirements (must be done before any returns)
  let hasRequiredRole = true;
  if (roles && roles.length > 0) {
    if (requireAll) {
      hasRequiredRole = hasAllRoles(roles);
    } else {
      hasRequiredRole = hasAnyRole(roles);
    }
  }

  // All hooks must be called before any conditional returns
  useEffect(() => {
    if (!loading && !isAuthenticated) {
      showError('Please login to access this page.');
    }
  }, [loading, isAuthenticated, showError]);

  useEffect(() => {
    if (!loading && isAuthenticated && roles && roles.length > 0 && !hasRequiredRole) {
      showError(`Access denied. You need ${roles.join(' or ')} role to access this page.`);
    }
  }, [loading, isAuthenticated, hasRequiredRole, roles, showError]);

  // Show loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check role requirements if specified
  if (roles && roles.length > 0 && !hasRequiredRole) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-600 mb-4">Access Denied</h1>
          <p className="text-gray-600">
            You don't have permission to access this page.
          </p>
          <p className="text-sm text-gray-500 mt-2">
            Required role(s): {roles.join(', ')}
          </p>
        </div>
      </div>
    );
  }

  return children;
};

export default ProtectedRoute;
